import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAppContext } from '../../context/AppContext';
import axios from 'axios';
import getitLogo from '../../assets/logo-getit.png';
import totemPago from '../../assets/Totem-Get-it-pago.png';

const MP_URL = 'http://localhost:8001';
const MASTER_TIMEOUT_SECONDS = 90;

const OperateCardScreen = () => {
    const { total, items, clearCart } = useCart();
    const { sessionData, settings, paymentMethods, orderType, setOrderType } = useAppContext();
    const navigate = useNavigate();

    const [secondsLeft, setSecondsLeft] = useState(MASTER_TIMEOUT_SECONDS);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalType, setErrorModalType] = useState('error');
    const [errorModalMessage, setErrorModalMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [printStatus, setPrintStatus] = useState('');

    const PAYMENT_SESSION_KEY = `payment_session_${sessionData?.session_id || 'default'}`;

    const processRef = useRef(false);
    const navigationRef = useRef(false);
    const intentIdRef = useRef(null);
    const secondsLeftRef = useRef(MASTER_TIMEOUT_SECONDS);
    const countdownRef = useRef(null);
    const cancelledRef = useRef(false);
    const pollRef = useRef(null);
    const ticketRef = useRef(null);
    const amountRef = useRef(null);
    const customerWhatsappRef = useRef(null);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem(PAYMENT_SESSION_KEY + '_started');
            sessionStorage.removeItem(PAYMENT_SESSION_KEY + '_completed');
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [PAYMENT_SESSION_KEY]);

    const showError = useCallback((type, message) => {
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setErrorModalType(type);
        setErrorModalMessage(message);
        setShowErrorModal(true);
        setIsProcessing(false);
    }, []);

    const cancelPayment = useCallback(async (intentId) => {
        if (!intentId) return;
        try {
            await axios.post(`${MP_URL}/mp_cancel/${intentId}`, {}, { timeout: 5000 });
        } catch (e) {
            console.warn('Error al cancelar en MP:', e.message);
        }
    }, []);

    const getMpPaymentMethodId = useCallback((paymentMethodType = '') => {
        const isDebit = paymentMethodType.toLowerCase().includes('debit');
        const allMethods = paymentMethods || [];

        console.log(`[PAYMENT] type="${paymentMethodType}" isDebit=${isDebit}`);
        console.log(`[PAYMENT] Métodos disponibles: ${JSON.stringify(allMethods.map(m => ({ id: m.id, name: m.name })))}`);

        const n = (name) => (name || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

        // Débito → busca método cuyo nombre sea exactamente "Débito" o contenga "debito"
        // Crédito → busca método cuyo nombre sea exactamente "Crédito" o contenga "credito"
        const keyword = isDebit ? 'debito' : 'credito';
        const specificMethod = allMethods.find(m => n(m.name).includes(keyword));

        if (specificMethod) {
            console.log(`[PAYMENT] Método encontrado: "${specificMethod.name}" (id: ${specificMethod.id})`);
            return specificMethod.id;
        }

        // Fallback: Mercado Pago genérico
        const mpMethod = allMethods.find(m => n(m.name).includes('mercado') || n(m.name) === 'mp');
        const id = mpMethod?.id || 11;
        console.warn(`[PAYMENT] Fallback: "${mpMethod?.name || 'id=11'}" (id: ${id})`);
        return id;
    }, [paymentMethods]);

    const doCreateOrder = useCallback(async (intentId, ticket, customerWhatsapp, amount, mpVoucher) => {
        let totalComboSavings = 0;
        const products = items.flatMap((item) => {
            const itemId = String(item.id);
            const productId = itemId.includes('-') ? itemId.split('-')[0] : itemId;
            const basePrice = item.base_price || item.price;
            const qty = parseInt(item.quantity);
            const price = parseFloat(basePrice);

            const productData = {
                product_id: parseInt(productId),
                name: item.name,
                price: price,
                price_subtotal: price * qty,
                qty: qty,
                customization: item.customDescription || '',
                category_id: item.category_id || null,
                category_name: item.category_name || '',
                attribute_lines: item.attribute_lines || [],
            };

            if (item.selectedAttributes && Object.keys(item.selectedAttributes).length > 0) {
                productData.selected_attributes = JSON.stringify(item.selectedAttributes);
            }

            if (item.drinkSelection) {
                const drink = item.drinkSelection.drink;
                const comboPrice = parseFloat(item.drinkSelection.combo_price);
                const drinkPrice = parseFloat(drink.price);
                const savings = (price + drinkPrice - comboPrice) * qty;
                totalComboSavings += savings;

                const drinkData = {
                    product_id: drink.id,
                    name: drink.name,
                    price: drinkPrice,
                    price_subtotal: drinkPrice * qty,
                    qty: qty,
                    customization: '',
                    attribute_lines: drink.attribute_lines || [],
                    is_combo_drink: true,
                };
                return [productData, drinkData];
            }

            return [productData];
        });

        const orderBody = {
            session_id: parseInt(sessionData.session_id),
            amount: amount,
            ticket: ticket,
            intent_id: intentId,
            whatsapp_number: customerWhatsapp || null,
            session_data: {
                company_data: {
                    name: sessionData.company_data?.name || 'AUTOSERVICIO',
                    vat: sessionData.company_data?.vat || '',
                    street: sessionData.company_data?.street || 'N/A',
                    city: sessionData.company_data?.city || 'N/A',
                    turn: sessionData.company_data?.turn || 'COMERCIO',
                    acteco: sessionData.company_data?.acteco || '471100'
                }
            },
            orders: [{
                products: products,
                payment: [{
                    id: getMpPaymentMethodId(mpVoucher?.payment_method_type || ''),
                    monto: amount,
                    payment_method_type: mpVoucher?.payment_method_type || '',
                    U_Id_Terminal_POS: 'POS-MP',
                    CreditCard: mpVoucher?.payment_method_type?.includes('debit') ? 'DB' : mpVoucher?.payment_method_type?.includes('credit') ? 'CR' : (mpVoucher?.payment_method_type || ''),
                    CrCardNum: mpVoucher?.last_four_digits || '',
                    OwnerIdNum: String(mpVoucher?.authorization_code || mpVoucher?.payment_id || ''),
                    ConfNum: String(mpVoucher?.authorization_code || mpVoucher?.payment_id || ''),
                    NumOfPmnts: String(mpVoucher?.installments || 1),
                    transaction_id: String(mpVoucher?.id || ''),
                    transbank_response: JSON.stringify({ mp_intent_id: String(mpVoucher?.id || '') })
                }],
                customer_comment: orderType === 'llevar' ? '[PARA LLEVAR]' : '[PARA SERVIR]',
                ...(totalComboSavings > 0 && { dsc_rcg_global: Math.round(totalComboSavings) })
            }]
        };

        try {
            const apiUrl = settings?.apiUrl || 'http://localhost:9000';
            console.log('═══════════════════════════════════════');
            console.log('[PROXY] POST /api/autoservicio/create-order');
            console.log('[PROXY] intent_id:', intentId, '| amount:', amount, '| ticket:', ticket);
            console.log('[PROXY] products:', orderBody.orders[0].products.length);
            const response = await axios.post(`${apiUrl}/api/autoservicio/create-order`, orderBody, {
                timeout: 60000,
                headers: { 'Content-Type': 'application/json' }
            });
            console.log('[PROXY] Respuesta create-order:', JSON.stringify(response.data));

            if (!response.data.success) {
                throw new Error(response.data.error || 'Error desconocido');
            }

            const orderNumber = response.data.order_number || '';
            console.log(`[PROXY] ✓ Orden creada - dte_folio: ${response.data.dte_folio} | order_number: ${orderNumber} | dte_ted: ${response.data.dte_ted ? `SI (${response.data.dte_ted.length} chars)` : 'NO'}`);
            console.log('═══════════════════════════════════════');

            const now = new Date();
            const horaStr = now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

            const dtefolio = response.data.dte_folio;
            const dteTed = response.data.dte_ted || null;
            if (dtefolio) {
                const companyData = sessionData.company_data || {};
                const neto = Math.round(amount / 1.19);
                const iva = amount - neto;
                const printBody = {
                    folio: dtefolio,
                    rut: companyData.vat || '',
                    razon_social: companyData.name || 'AUTOSERVICIO',
                    giro: companyData.turn || 'COMERCIO',
                    direccion: companyData.street || 'N/A',
                    ciudad: companyData.city || 'N/A',
                    fecha: now.toISOString().split('T')[0],
                    hora: horaStr,
                    items: items.flatMap(item => {
                        const qty = parseFloat(item.quantity) || 1;
                        const unitPrice = parseFloat(item.base_price || item.price);
                        const totalItemPrice = parseFloat(item.price);
                        const extraAmount = Math.round((totalItemPrice - unitPrice) * qty);
                        const result = [{ nombre: item.name, cantidad: qty, precio_unitario: unitPrice, total: unitPrice * qty }];
                        if (extraAmount > 0) {
                            result.push({ nombre: 'EXTRAS', cantidad: 1, precio_unitario: extraAmount, total: extraAmount });
                        }
                        if (item.drinkSelection) {
                            const drink = item.drinkSelection.drink;
                            const drinkPrice = parseFloat(drink.price);
                            result.push({ nombre: drink.name, cantidad: qty, precio_unitario: drinkPrice, total: drinkPrice * qty });
                        }
                        return result;
                    }),
                    neto,
                    iva,
                    total: amount,
                    ...(totalComboSavings > 0 && { descuento: Math.round(totalComboSavings) }),
                    ...(dteTed && { ted: dteTed }),
                    voucher: mpVoucher || null
                };

                setPrintStatus('Imprimiendo...');
                try {
                    const xsignUrl = settings?.xsignUrl || 'http://localhost:5999';

                    let voucherMp = null;
                    if (mpVoucher) {
                        const tipoRaw = mpVoucher.payment_method_type || '';
                        const nombreRaw = (mpVoucher.payment_method_name || '').toUpperCase();
                        const tipoLabel = tipoRaw.includes('credit') ? 'CRÉDITO' : tipoRaw.includes('debit') ? 'DÉBITO' : 'TARJETA';
                        voucherMp = {
                            terminal: 'POS-MP',
                            orden: `#${ticket}`,
                            tipo: `${tipoLabel} ${nombreRaw}`.trim(),
                            ultimos_cuatro: mpVoucher.last_four_digits || '****',
                            cuotas: mpVoucher.installments || 1,
                            monto: mpVoucher.transaction_amount || amount,
                            cod_autorizacion: mpVoucher.authorization_code || '',
                            id_operacion: String(mpVoucher.id || ''),
                            estado: 'APROBADO'
                        };
                    }

                    const printAllBody = {
                        ...printBody,
                        ...(voucherMp && { voucher_mp: voucherMp }),
                        ...(orderNumber && { order_number: orderNumber }),
                        order_type: orderType === 'llevar' ? 'PARA LLEVAR' : 'PARA SERVIR',
                    };

                    console.log('[PRINT] POST /print_all - folio:', dtefolio, '| voucher:', !!voucherMp, '| order:', orderNumber);
                    const printRes = await axios.post(`${xsignUrl}/print_all`, printAllBody, { timeout: 15000 });
                    console.log('[PRINT] ✓ Respuesta /print_all:', JSON.stringify(printRes.data));
                } catch (printErr) {
                    console.warn('[PRINT] ✗ Error en /print_all (venta continúa):', printErr.message);
                }
            }

            sessionStorage.setItem(PAYMENT_SESSION_KEY + '_completed', 'true');
            navigationRef.current = true;
            clearCart();
            setOrderType(null);
            navigate('/thank-you', { replace: true, state: { orderNumber } });

        } catch (err) {
            console.error('Error creando orden:', err);
            cancelledRef.current = true;
            showError('error', err.response?.data?.error || err.message || 'Error al registrar la orden.');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, sessionData, settings, clearCart, navigate, PAYMENT_SESSION_KEY, showError, setPrintStatus, getMpPaymentMethodId, orderType, setOrderType]);

    const handlePayment = useCallback(async () => {
        if (processRef.current || navigationRef.current) return;
        if (sessionStorage.getItem(PAYMENT_SESSION_KEY + '_completed') === 'true') return;

        processRef.current = true;
        cancelledRef.current = false;
        sessionStorage.setItem(PAYMENT_SESSION_KEY + '_started', 'true');
        setIsProcessing(true);

        secondsLeftRef.current = MASTER_TIMEOUT_SECONDS;
        setSecondsLeft(MASTER_TIMEOUT_SECONDS);

        const ticket = Math.floor(Math.random() * 90000) + 10000;
        const customerWhatsapp = sessionStorage.getItem('customer_whatsapp');
        const amount = Math.round(total);

        ticketRef.current = ticket;
        amountRef.current = amount;
        customerWhatsappRef.current = customerWhatsapp;

        // Paso 1: Iniciar pago en MP
        console.log('═══════════════════════════════════════');
        console.log('[MP] INICIO FLUJO DE PAGO');
        console.log(`[MP] Monto: $${amount} | Ticket: ${ticket}`);
        console.log(`[MP] URL: ${MP_URL}/mp_pay`);
        let intentId;
        try {
            const mpPayload = { amount: String(amount), reference: `POS-${ticket}` };
            console.log('[MP] POST /mp_pay payload:', JSON.stringify(mpPayload));
            const mpRes = await axios.post(`${MP_URL}/mp_pay`, mpPayload, { timeout: 10000 });
            console.log('[MP] Respuesta /mp_pay:', JSON.stringify(mpRes.data));

            intentId = mpRes.data?.id;
            if (!intentId) throw new Error('MP no retornó ID de orden');
            intentIdRef.current = intentId;
            console.log(`[MP] ✓ Orden creada - intent_id: ${intentId}`);
            console.log('═══════════════════════════════════════');
        } catch (err) {
            console.error('[MP] ✗ Error iniciando pago:', err.message);
            if (err.response) console.error('[MP] Response error:', JSON.stringify(err.response.data));
            showError('error', 'No se pudo conectar con el terminal de pago. Intente nuevamente.');
            processRef.current = false;
            return;
        }

        // Paso 2: Countdown
        countdownRef.current = setInterval(() => {
            secondsLeftRef.current -= 1;
            setSecondsLeft(secondsLeftRef.current);

            if (secondsLeftRef.current <= 0) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                if (!cancelledRef.current && !navigationRef.current) {
                    cancelledRef.current = true;
                    cancelPayment(intentIdRef.current);
                    showError('timeout', 'Tiempo de espera agotado. Si realizaste el pago, contacta al administrador.');
                }
            }
        }, 1000);

        // Paso 3: Polling de estado MP
        pollRef.current = setInterval(async () => {
            if (cancelledRef.current || navigationRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                return;
            }

            try {
                const statusRes = await axios.get(`${MP_URL}/mp_status/${intentId}`, { timeout: 5000 });
                const data = statusRes.data;
                const status = data?.status;
                const payments = data?.payments || data?.transactions?.payments || [];
                const approvedPayment = payments.find(p => p.status === 'approved');
                const isSuccess = (status === 'closed' && approvedPayment) || status === 'processed';

                console.log(`[MP POLL] status: ${status} | payments: ${payments.length} | approved: ${!!approvedPayment} | success: ${!!isSuccess}`);
                if (status === 'processed' || status === 'closed') {
                    console.log('[MP POLL] Respuesta completa:', JSON.stringify(data));
                }
                if (payments.length > 0) {
                    payments.forEach((p, i) => {
                        console.log(`[MP POLL] payment[${i}]: id=${p.id} status=${p.status} method=${p.payment_method_id} amount=${p.transaction_amount}`);
                    });
                }

                if (isSuccess) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                    if (cancelledRef.current || navigationRef.current) return;
                    navigationRef.current = true;
                    setPrintStatus('Pago aprobado\nGenerando comprobante...');

                    const paymentInfo = approvedPayment || payments[0] || {};
                    const methodId = paymentInfo.payment_method?.id || paymentInfo.payment_method_id || 'tarjeta';
                    const paidAmount = paymentInfo.paid_amount || paymentInfo.transaction_amount || paymentInfo.amount || amount;

                    console.log('═══════════════════════════════════════');
                    console.log('[MP] ✓ PAGO APROBADO');
                    console.log(`[MP] ID orden: ${data.id}`);
                    console.log(`[MP] Método: ${methodId}`);
                    console.log(`[MP] Monto pagado: ${paidAmount}`);
                    console.log(`[MP] Status detail: ${data.status_detail}`);
                    console.log('═══════════════════════════════════════');

                    const last4 = paymentInfo.card?.last_four_digits || paymentInfo.last_four_digits || '****';
                    const safeNumericCode = String(Math.floor(100000 + Math.random() * 900000));

                    const mpVoucher = {
                        id: data.id,
                        payment_id: paymentInfo.id || '',
                        payment_method_id: methodId,
                        transaction_amount: parseInt(paidAmount) || amount,
                        date_approved: new Date().toISOString(),
                        last_four_digits: last4,
                        authorization_code: paymentInfo.authorization_code || safeNumericCode,
                        installments: paymentInfo.installments || 0,
                        payment_method_type: paymentInfo.payment_method?.type || '',
                        payment_method_name: paymentInfo.payment_method?.id || methodId
                    };

                    await doCreateOrder(intentId, ticket, customerWhatsapp, amount, mpVoucher);

                } else if (status === 'canceled' || status === 'cancelled' || status === 'expired') {
                    console.warn(`[MP] ✗ Pago no completado - status: ${status}`);
                    console.log('[MP] Respuesta completa:', JSON.stringify(data));
                    clearInterval(pollRef.current); pollRef.current = null;
                    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                    if (!cancelledRef.current) {
                        cancelledRef.current = true;
                        showError('error', 'El pago fue cancelado en el terminal. Puede intentarlo nuevamente.');
                    }
                } else if (status === 'failed') {
                    console.warn(`[MP] ✗ Pago fallido - status: ${status}`);
                    console.log('[MP] Respuesta completa:', JSON.stringify(data));
                    clearInterval(pollRef.current); pollRef.current = null;
                    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                    if (!cancelledRef.current) {
                        cancelledRef.current = true;
                        showError('error', 'El pago fue rechazado. Por favor intente nuevamente o use otro medio de pago.');
                    }
                }
            } catch (e) {
                console.warn('[MP POLL] Error:', e.message);
                if (e.response) console.warn('[MP POLL] Response:', JSON.stringify(e.response.data));
            }
        }, 3000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total, PAYMENT_SESSION_KEY, showError, cancelPayment, doCreateOrder]);


    useEffect(() => {
        const alreadyStarted = sessionStorage.getItem(PAYMENT_SESSION_KEY + '_started') === 'true';
        const alreadyCompleted = sessionStorage.getItem(PAYMENT_SESSION_KEY + '_completed') === 'true';
        if (alreadyCompleted || alreadyStarted || processRef.current) return;

        const timer = setTimeout(() => {
            if (!processRef.current) handlePayment();
        }, 300);

        return () => clearTimeout(timer);
    }, [PAYMENT_SESSION_KEY, handlePayment]);

    const handleModalClose = () => {
        setShowErrorModal(false);
        navigate('/payment-method');
    };

    return (
        <div style={{
            height: '100vh',
            overflow: 'hidden',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                position: 'relative'
            }}>
                <button
                    onClick={() => !isProcessing && navigate('/payment-method')}
                    disabled={isProcessing}
                    style={{
                        position: 'absolute',
                        left: '1.5rem',
                        background: 'none',
                        border: 'none',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.3 : 0.7,
                        padding: '0.5rem'
                    }}
                >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                    </svg>
                </button>

                <img src={getitLogo} alt="GetIT" style={{ height: '100px', width: 'auto' }} />

            </div>

            {/* Content */}
            <div style={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.5rem 4rem',
                gap: '1.5rem'
            }}>
                {/* Monto */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '20px',
                    padding: '1.5rem 5rem',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '1.5rem', color: '#888', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Total a pagar
                    </div>
                    <div style={{ fontSize: '4.5rem', fontWeight: '900', color: '#1a1a1a', lineHeight: 1 }}>
                        ${total.toLocaleString()}
                    </div>
                </div>

                {/* Imagen totem + texto superpuesto */}
                <div style={{ position: 'relative', display: 'inline-block', flex: 1, overflow: 'hidden' }}>
                    <img
                        src={totemPago}
                        alt="Inserte su tarjeta"
                        style={{
                            maxHeight: 'calc(100vh - 260px)',
                            width: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto'
                        }}
                    />

                    {/* Instrucción sobre zona transparente inferior */}
                    <div style={{
                        position: 'absolute',
                        bottom: '2rem',
                        left: 0,
                        right: 0,
                        textAlign: 'center'
                    }}>
                        {printStatus ? (
                            <p style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: '2.5rem',
                                fontWeight: '900',
                                color: '#0E8164',
                                margin: 0,
                                lineHeight: '1.4'
                            }}>
                                {printStatus}
                            </p>
                        ) : (
                            <p style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: '3rem',
                                fontWeight: '900',
                                color: '#0E8164',
                                margin: 0,
                                lineHeight: '1.3'
                            }}>
                                Inserte su tarjeta<br />y siga las instrucciones.
                            </p>
                        )}

                        {/* Master timeout — solo visible cuando queda poco tiempo */}
                        {isProcessing && secondsLeft <= 30 && (
                            <div style={{
                                marginTop: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                color: secondsLeft <= 15 ? '#dc2626' : '#f59e0b'
                            }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12,1A11,11 0 0,0 1,12A11,11 0 0,0 12,23A11,11 0 0,0 23,12A11,11 0 0,0 12,1M12,21A9,9 0 0,1 3,12A9,9 0 0,1 12,3A9,9 0 0,1 21,12A9,9 0 0,1 12,21M12.5,7H11V13L16.25,16.15L17,14.92L12.5,12.25V7Z" />
                                </svg>
                                <span style={{ fontSize: '2rem', fontWeight: '700' }}>
                                    Tienes {secondsLeft}s para completar el pago
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de error flotante */}
            {showErrorModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.65)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '2rem'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '28px',
                        padding: '4rem 3rem',
                        maxWidth: '640px',
                        width: '100%',
                        textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }}>
                        {/* Icono */}
                        <div style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '50%',
                            background: errorModalType === 'timeout'
                                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                : 'linear-gradient(135deg, #ef4444, #dc2626)',
                            margin: '0 auto 2rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {errorModalType === 'timeout' ? (
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
                                    <path d="M12,1A11,11 0 0,0 1,12A11,11 0 0,0 12,23A11,11 0 0,0 23,12A11,11 0 0,0 12,1M12,21A9,9 0 0,1 3,12A9,9 0 0,1 12,3A9,9 0 0,1 21,12A9,9 0 0,1 12,21M12.5,7H11V13L16.25,16.15L17,14.92L12.5,12.25V7Z" />
                                </svg>
                            ) : (
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
                                    <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                                </svg>
                            )}
                        </div>

                        <h2 style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '3rem',
                            fontWeight: '900',
                            color: '#1f2937',
                            marginBottom: '1rem'
                        }}>
                            {errorModalType === 'timeout' ? 'Tiempo Agotado' : 'Error en el Pago'}
                        </h2>

                        <p style={{
                            fontSize: '1.6rem',
                            color: '#6b7280',
                            marginBottom: '2.5rem',
                            lineHeight: '1.5'
                        }}>
                            {errorModalMessage}
                        </p>

                        <button
                            onClick={handleModalClose}
                            style={{
                                width: '100%',
                                padding: '1.5rem',
                                background: '#0E8164',
                                color: 'white',
                                border: 'none',
                                borderRadius: '16px',
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            Volver e Intentar Nuevamente
                        </button>
                    </div>
                </div>
            )}
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@900&display=swap');`}</style>
        </div>
    );
};

export default OperateCardScreen;
